import { ServerEvents } from "@/types/ServerEvents";
import { CopyModelMaterialRequest, EtlJobStatus, Job, JobStatus, ProgressUpdate, ServerNotificationType } from "@api/apiClient";
import * as signalR from "@microsoft/signalr";

const URL = `${window.location.origin}/api/signalr/carbontool`.replace("5000", "3000"); //or whatever your backend port is
console.log("URL", URL);

class Connector {
  // eslint-disable-next-line no-use-before-define
  static instance: Connector;
  public eventHandlers: (serverEvents: ServerEvents) => void;
  private connection: signalR.HubConnection;

  constructor() {
    this.connection = new signalR.HubConnectionBuilder().withUrl(URL, { withCredentials: true }).configureLogging(signalR.LogLevel.Error).withAutomaticReconnect().build();

    this.connection.start().catch((err) => {
      console.error(err.toString());
    });
    this.eventHandlers = (serverEvents: ServerEvents) => {
      this.connection.on(ServerNotificationType.OnJobComplete, async (jobId: string, job: Job) => {
        await serverEvents.onJobComplete?.(jobId, job);
      });
      this.connection.on(ServerNotificationType.OnJobStatus, async (jobId: string, jobStatus: JobStatus) => {
        await serverEvents.onJobStatus?.(jobId, jobStatus);
      });
      this.connection.on(ServerNotificationType.OnJobFailure, async (jobId: string, jobStatus: JobStatus) => {
        await serverEvents.onJobFailure?.(jobId, jobStatus);
      });
      this.connection.on(ServerNotificationType.OnJobProgress, async (jobId: string, progressUpdate: ProgressUpdate) => {
        await serverEvents.onJobProgress?.(jobId, progressUpdate);
      });
      this.connection.on(ServerNotificationType.OnExcelExportStart, async (projectId: string, jobId: string) => {
        await serverEvents.onExcelExportStart?.(projectId, jobId);
      });
      this.connection.on(ServerNotificationType.OnExcelExportComplete, async (projectId: string, jobId: string) => {
        await serverEvents.onExcelExportComplete?.(projectId, jobId);
      });
      this.connection.on(ServerNotificationType.OnExcelExportFailure, async (projectId: string, jobId: string) => {
        await serverEvents.onExcelExportFailure?.(projectId, jobId);
      });
      this.connection.on(ServerNotificationType.OnModelImportJobStatus, async (jobId: string, fileName: string, jobStatus: EtlJobStatus) => {
        await serverEvents.onModelImportJobStatus?.(jobId, fileName, jobStatus);
      });
      this.connection.on(ServerNotificationType.OnModelImportJobComplete, async (jobId: string, fileName: string, jobStatus: EtlJobStatus) => {
        await serverEvents.onModelImportJobComplete?.(jobId, fileName, jobStatus);
      });
      this.connection.on(ServerNotificationType.OnReceiveMessage, async (message) => {
        console.log("received message: ", message);
      });
      this.connection.on(ServerNotificationType.OnCloneScenarioStatus, async (scenarioName: string) => {
        await serverEvents.onCloneScenarioStatus?.(scenarioName);
      });
      this.connection.on(ServerNotificationType.OnCloneScenarioComplete, async (scenarioName: string) => {
        await serverEvents.onCloneScenarioComplete?.(scenarioName);
      });
      this.connection.on(ServerNotificationType.OnCloneScenarioFailure, async (scenarioName: string) => {
        await serverEvents.onCloneScenarioFailure?.(scenarioName);
      });
      this.connection.on(ServerNotificationType.OnCopyModelMaterialStatus, async (modelName: string) => {
        await serverEvents.onCopyModelMaterialStatus?.(modelName);
      });
      this.connection.on(ServerNotificationType.OnCopyModelMaterialComplete, async (modelName: CopyModelMaterialRequest) => {
        await serverEvents.onCopyModelMaterialComplete?.(modelName);
      });
      this.connection.on(ServerNotificationType.OnCopyModelMaterialFailure, async (modelName: string) => {
        await serverEvents.onCopyModelMaterialFailure?.(modelName);
      });
      this.connection.on(ServerNotificationType.OnApsUsersSyncStatus, async () => {
        await serverEvents.onApsUsersSyncStatus?.();
      });
      this.connection.on(ServerNotificationType.OnApsUsersSyncComplete, async () => {
        await serverEvents.onApsUsersSyncComplete?.();
      });
      this.connection.on(ServerNotificationType.OnApsUsersSyncFailure, async () => {
        await serverEvents.onApsUsersSyncFailure?.();
      });
      this.connection.on(ServerNotificationType.OnDeleteProjectComplete, async (projectId: string, projectName: string) => {
        await serverEvents.onDeleteProjectComplete?.(projectId, projectName);
      });
      this.connection.on(ServerNotificationType.OnDeleteProjectFailure, async (projectId: string, projectName: string) => {
        await serverEvents.onDeleteProjectFailure?.(projectId, projectName);
      });
      this.connection.on(ServerNotificationType.OnModelSplitStart, async (projectId: string, modelId: string) => {
        await serverEvents.onModelSplitStart?.(projectId, modelId);
      });
      this.connection.on(ServerNotificationType.OnModelSplitComplete, async (projectId: string, modelId: string) => {
        await serverEvents.onModelSplitComplete?.(projectId, modelId);
      });
      this.connection.on(ServerNotificationType.OnModelSplitFailure, async (projectId: string, modelId: string) => {
        await serverEvents.onModelSplitFailure?.(projectId, modelId);
      });
      this.connection.on(ServerNotificationType.OnCreateVersionStatus, async (message: string) => {
        await serverEvents.onCreateVersionStatus?.(message);
      });
      this.connection.on(ServerNotificationType.OnCreateVersionComplete, async (response: any) => {
        await serverEvents.onCreateVersionComplete?.(response);
      });
      this.connection.on(ServerNotificationType.OnCreateVersionFailure, async (message: string) => {
        await serverEvents.onCreateVersionFailure?.(message);
      });
      this.connection.on(ServerNotificationType.OnDeleteModelStatus, async (message: string) => {
        await serverEvents.onDeleteModelStatus?.(message);
      });
      this.connection.on(ServerNotificationType.OnDeleteModelComplete, async (response: any) => {
        await serverEvents.onDeleteModelComplete?.(response);
      });
      this.connection.on(ServerNotificationType.OnDeleteModelFailure, async (message: string) => {
        await serverEvents.onDeleteModelFailure?.(message);
      });
      this.connection.on(ServerNotificationType.OnUpdateVersionStatus, async (message: string) => {
        await serverEvents.onUpdateVersionStatus?.(message);
      });
      this.connection.on(ServerNotificationType.OnUpdateVersionComplete, async (response: any) => {
        await serverEvents.onUpdateVersionComplete?.(response);
      });
      this.connection.on(ServerNotificationType.OnUpdateVersionFailure, async (response: any) => {
        await serverEvents.onUpdateVersionFailure?.(response);
      });
    };
  }

  sendMessage = (messages: string) => {
    this.connection.send("sendMessage", "foo", messages).then((_x: any) => console.log("sent"));
  };

  static getInstance(): Connector {
    if (!Connector.instance) {
      Connector.instance = new Connector();
    }
    return Connector.instance;
  }
}

export default Connector.getInstance;
